import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import {
	getPostTargetIdentity,
	isScheduledOrPendingOrDraftOrNew,
	targetMatchesConnection,
} from '../../../../models/UiUtils';
import {
	getPruneSocialPostTargetErrorMessage,
	invalidateAllSocialMediaPostReports,
	usePruneSocialPostTargetMutation,
} from '../../../../queries';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { SocialMediaPlatformTableType } from './models';
import { SocialMediaPlatformRow } from './presentation';
import { styleSheet } from './styles';

const RemoveTargetConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Remove',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

interface IProps {
	className?: string;
	connections?: Api.ISocialMediaConnection[];
	impersonationContext?: Api.IImpersonationContext;
	onOrphanedTargetRemoved?(connection: Api.ISocialMediaConnection): void;
	onSelectionChanged?(selectedTargets: Api.IPostTarget[]): void;
	source?: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	styles?: StyleDeclarationValue[];
	type: SocialMediaPlatformTableType;
}

export const SocialMediaPlatformTable: React.FC<IProps> = observer(props => {
	const {
		className,
		styles,
		connections,
		impersonationContext,
		onOrphanedTargetRemoved,
		onSelectionChanged,
		type,
		source,
	} = props;
	const { hasChanges, postTargets, setHasChanges, setPostTargets } = useEditSocialMediaPostContext();
	const { logApiError } = useEventLogging('SocialMediaPlatformTable');
	const [connectionToRemove, setConnectionToRemove] = React.useState<Api.ISocialMediaConnection>(null);
	const [removingConnection, setRemovingConnection] = React.useState<Api.ISocialMediaConnection>(null);
	const pruneTargetMutation = usePruneSocialPostTargetMutation();
	const onSelectedChanged = React.useCallback(
		(selected: boolean, connection: Api.ISocialMediaConnection) => {
			const conns = postTargets ? [...postTargets] : [];

			if (selected && !conns.find(x => x?.provider === connection?.type && x?.pageId === connection?.postTargetId)) {
				conns.push({ pageId: connection.postTargetId, provider: connection.type, userId: connection.userId });
				onSelectionChanged?.(conns);
				return;
			}

			if (!selected) {
				onSelectionChanged?.(conns.filter(x => x?.pageId !== connection?.postTargetId) || []);
			}
		},
		[onSelectionChanged, postTargets]
	);

	const removeOrphanedTarget = React.useCallback(
		async (connection: Api.ISocialMediaConnection) => {
			const target = getPostTargetIdentity(connection, postTargets);
			// One prune at a time: a second in-flight prune would answer with targets captured before the
			// first one landed, and adopting that response would re-add the target we just removed.
			if (!source?.id || !target || removingConnection) {
				return;
			}

			setRemovingConnection(connection);
			const hadUnsavedChanges = hasChanges;
			try {
				const updatedPost = await pruneTargetMutation.mutateAsync({
					impersonationContext,
					postId: source.id,
					target,
				});

				// The removal is already persisted, so update local selection directly instead of routing
				// through onSelectionChanged — that would flag unsaved changes and ask for a reschedule.
				// Prefer the server's remaining targets so a later save can't re-add what it just pruned.
				const remainingTargets = updatedPost?.designatedTargets;
				setPostTargets?.(current =>
					remainingTargets?.length
						? remainingTargets
						: (current ?? []).filter(x => !targetMatchesConnection(x, connection))
				);
				// setPostTargets clears the dirty flag, which would drop the unsaved-changes guard on edits the
				// user made before removing the target or while the prune was in flight.
				setHasChanges?.(current => current || hadUnsavedChanges);
				onOrphanedTargetRemoved?.(connection);
				invalidateAllSocialMediaPostReports();
				toast.success('Removed the disconnected account from this post.');
			} catch (err) {
				logApiError('PruneSocialMediaPostTarget-Error', err);
				toast.error(getPruneSocialPostTargetErrorMessage(err as Api.IOperationResultNoValue));
			} finally {
				setRemovingConnection(null);
			}
		},
		[
			hasChanges,
			impersonationContext,
			logApiError,
			onOrphanedTargetRemoved,
			postTargets,
			pruneTargetMutation,
			removingConnection,
			setHasChanges,
			setPostTargets,
			source?.id,
		]
	);

	const onRemoveConfirmationRequestClose = React.useCallback(
		(result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			const connection = connectionToRemove;
			setConnectionToRemove(null);
			if (connection && !canceled && result?.representedObject) {
				removeOrphanedTarget(connection);
			}
		},
		[connectionToRemove, removeOrphanedTarget]
	);

	const multipleConnectionsExist = isScheduledOrPendingOrDraftOrNew(source)
		? Api.SupportedSocialMediaTypes.map(x => {
				return connections?.filter(y => y?.type === x)?.length > 1;
			}).includes(true)
		: Api.SupportedSocialMediaTypes.map(x => {
				return postTargets?.filter(y => y?.provider === x)?.length > 1;
			}).includes(true);

	if (!postTargets) {
		return null;
	}

	return (
		<section className='social-media-reporting-table'>
			<article className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				<p className={css(styleSheet.tableHeaderElement, styleSheet.names)}>Share on selected platform(s)</p>
				<p className={css(styleSheet.tableHeaderElement, styleSheet.accountLogin)}> Connected account(s)</p>
				<p className={css(styleSheet.tableHeaderElement, styleSheet.postLocation)}> Post location(s)</p>
			</article>
			{Api.SupportedSocialMediaTypes.map((socialType, j) => {
				return (
					<div className={css(styleSheet.typeContainer)} key={j}>
						{connections
							?.sort((u, v) => (u.postTargetId > v.postTargetId ? 1 : -1))
							?.sort((a, b) => (a.type === b.type ? 0 : a.type > b.type ? 1 : -1))
							?.filter(x => x?.type === socialType)
							?.map((connection, i) => {
								// sets the postTarget to the connection type if it exists
								const postTarget = postTargets.find(
									x =>
										x?.provider === connection?.type &&
										(x?.state?.lastConnectionState?.postTargetId === connection?.postTargetId ||
											x?.pageId === connection?.postTargetId)
								);
								// Identifies if the connection is selected
								const selected = Boolean(Object.keys(postTarget || {})?.length);

								return (
									<SocialMediaPlatformRow
										connection={connection}
										isRemovePending={!!removingConnection}
										isRemovingTarget={removingConnection === connection}
										key={`${connection?.type}-${connection?.postTargetId}-${i}`}
										onRemoveOrphanedTargetClicked={onOrphanedTargetRemoved ? setConnectionToRemove : undefined}
										onSelectedChanged={onSelectedChanged}
										postSource={source}
										postTarget={postTarget}
										selected={selected}
										type={type}
									/>
								);
							})}
						{socialType && multipleConnectionsExist && connections?.find(x => x.type === socialType) && (
							<div className={css(styleSheet.spacer)} />
						)}
					</div>
				);
			})}
			{connectionToRemove ? (
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{ isOpen: true, onRequestClose: onRemoveConfirmationRequestClose }}
					options={RemoveTargetConfirmationOptions}
					title='Remove Disconnected Account'
				>
					<p>Remove this disconnected account? This post will no longer attempt to publish to it.</p>
				</ConfirmationDialog>
			) : null}
		</section>
	);
});
